import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { getNotices, getNoticesById } from '@/api/common';
import { Empty, ErrorBox, Loading, Modal, fmtDateTime, unwrap, PageTitle } from '../ui';

interface NoticeRow { id: number; title: string; excerpt: string; pinned: boolean; published_at: string | null; read: boolean }
interface NoticeFull { id: number; title: string; body: string; published_at: string | null }

function NoticeModal({ id, onClose }: { id: number; onClose: () => void }) {
  const q = useQuery({ queryKey: ['parent-notice', id], queryFn: () => unwrap<NoticeFull>(getNoticesById(id)) });
  return (
    <Modal title={q.data?.title ?? 'Notice'} onClose={onClose}>
      {q.isLoading ? <Loading /> : q.isError || !q.data ? <ErrorBox error={q.error} /> : (
        <>
          <p className="text-xs text-slate-500 mb-3">{q.data.published_at ? fmtDateTime(q.data.published_at) : ''}</p>
          <p className="text-sm whitespace-pre-wrap">{q.data.body}</p>
        </>
      )}
    </Modal>
  );
}

export function NoticesPage() {
  const qc = useQueryClient();
  const [unread, setUnread] = useState(false);
  const [open, setOpen] = useState<number | null>(null);
  const q = useQuery({ queryKey: ['parent-notices', unread], queryFn: () => unwrap<NoticeRow[]>(getNotices(unread ? { unread: 1 } : undefined)) });
  const close = () => { setOpen(null); qc.invalidateQueries({ predicate: (x) => ['parent-notices', 'parent-home'].includes(String(x.queryKey[0])) }); };

  return (
    <div className="space-y-5">
      <PageTitle kicker="School" title="Notices" sub="Announcements from the school. Tap one to read it." />
      <Card action={<Button variant="ghost" onClick={() => setUnread((v) => !v)}>{unread ? 'Show all' : 'Unread only'}</Button>}>
        {q.isLoading ? <Loading /> : q.isError ? <ErrorBox error={q.error} onRetry={() => q.refetch()} /> : !q.data?.length ? <Empty text="No notices." /> : (
          <ul className="divide-y">
            {q.data.map((n) => (
              <li key={n.id} className="py-3 cursor-pointer" onClick={() => setOpen(n.id)}>
                <div className={n.read ? '' : 'font-semibold'}>{n.pinned && '📌 '}{n.title}{!n.read && <span className="ml-2 text-xs text-brand-600">new</span>}</div>
                <div className="text-sm text-slate-500">{n.excerpt}</div>
                <div className="text-xs text-slate-400 mt-1">{n.published_at ? fmtDateTime(n.published_at) : ''}</div>
              </li>
            ))}
          </ul>
        )}
      </Card>
      {open && <NoticeModal id={open} onClose={close} />}
    </div>
  );
}
